import { mergeAuthHeaders, getAccessToken } from '../auth/tokenService';
import { resolveRequestUrl } from './config';
import {
  shouldAttachGlobalApiLoader,
  globalApiLoadingBegin,
  globalApiLoadingEnd,
} from './apiLoading';

let didWarnMissingToken = false;

/**
 * App-wide HTTP helper: same as `fetch`, but always adds `Authorization: Bearer <token>`
 * from stored credentials (cookie + sessionStorage mirror via {@link mergeAuthHeaders}).
 */
export function apiFetch(input, init = {}) {
  let base = {};
  if (init.headers instanceof Headers) {
    init.headers.forEach((value, key) => {
      base[key] = value;
    });
  } else if (init.headers && typeof init.headers === 'object') {
    base = { ...init.headers };
  }
  const merged = mergeAuthHeaders(base);
  if (!merged.Accept && !merged.accept) {
    merged.Accept = 'application/json';
  }
  if (!merged['Cache-Control']) {
    merged['Cache-Control'] = 'no-cache';
  }
  if (!merged.Pragma) {
    merged.Pragma = 'no-cache';
  }
  const resolvedInput =
    typeof input === 'string' && input.startsWith('/') ? resolveRequestUrl(input) : input;
  const requestUrl =
    typeof resolvedInput === 'string'
      ? resolvedInput
      : typeof resolvedInput?.url === 'string'
        ? resolvedInput.url
        : '';

  if (
    process.env.NODE_ENV === 'development' &&
    !didWarnMissingToken &&
    !merged.Authorization &&
    requestUrl &&
    (requestUrl.includes('/api/') || requestUrl.includes('royalcyber.org'))
  ) {
    didWarnMissingToken = true;
    console.warn(
      '[apiFetch] No access token stored — add ?token=… to the URL once or complete login. Request will have no Authorization header.',
    );
  }
  let crossOrigin = false;
  if (typeof window !== 'undefined' && requestUrl) {
    try {
      const u = new URL(requestUrl, window.location.href);
      crossOrigin = u.origin !== window.location.origin;
    } catch {
      /* ignore */
    }
  }

  const nextInit = { ...init };
  if (nextInit.credentials === undefined && crossOrigin) {
    nextInit.credentials = 'include';
  }

  const trackLoader = shouldAttachGlobalApiLoader(requestUrl, {
    skipGlobalLoader: init.skipGlobalLoader,
  });
  if (trackLoader) globalApiLoadingBegin();

  return fetch(resolvedInput, {
    ...nextInit,
    headers: new Headers(merged),
  })
    .finally(() => {
      if (trackLoader) globalApiLoadingEnd();
    })
    .then(async (response) => {
      const ct = (response.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('text/html')) {
        const hint =
          'The flyer API must be reached as JSON (gateway/proxy should forward /api/templates, /api/flyers, /api/health to Node before any SPA Path=/**). Build with REACT_APP_API_URL=https://<your-gateway-host> (no /api suffix; paths are appended).';
        throw new Error(
          `API returned HTML instead of JSON (HTTP ${response.status}). URL: ${requestUrl || '(request)'}. ${hint}`,
        );
      }
      return response;
    });
}
